import {
  type AddToCartMetadata,
  type CartGiftWithPurchaseAttributeValue,
  type CartGiftWithPurchaseConnection,
  type CartProductConnection,
  type CartProductVariant,
} from "../state";
import {
  type ComponentizableCartLine,
  type CartLine,
  type Maybe,
  type AttributeInput,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";
import { type PartialDeep } from "type-fest";
import { parseGid } from "@shopify/hydrogen-react";
import {
  CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS,
  CartLocation,
  EMPTY_ADD_TO_CART_METADATA,
  STOREFRONT_CART_ATTRIBUTES,
} from "@tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "./tablescapeContext";
// eslint-disable-next-line no-restricted-imports
import {
  formatProduct,
  getMsrpForVariant,
} from "@tablescape/utilities/product";

type CartLinePartial = PartialDeep<
  CartLine | ComponentizableCartLine,
  { recurseIntoArrays: true }
>;

/** Cart line shape used by GWP utilities; merchandise includes our GWP metafield fragment. */
type CartLineMerchandiseLike = PartialDeep<
  NonNullable<CartLinePartial["merchandise"]>,
  { recurseIntoArrays: true }
> & {
  giftWithPurchase?: CartGiftWithPurchaseConnection | null;
};

export type CartLineLike = Omit<CartLinePartial, "merchandise"> & {
  merchandise?: CartLineMerchandiseLike;
};

export const formatCartCollection = (collection: {
  products: {
    nodes: CartProductConnection[];
  };
}) => ({
  products: collection.products.nodes.map((node) => formatProduct(node)),
});

export type CartLineAttributesRead = {
  attributes?: Array<
    | {
        key?: string | null;
        value?: string | null;
      }
    | null
    | undefined
  > | null;
};

export const getCartLineSelectedOptions = (
  line: { merchandise?: unknown } | null | undefined
): SelectedOption[] | undefined => {
  const merchandise = line?.merchandise;
  if (merchandise == null || typeof merchandise !== "object") {
    return undefined;
  }

  if (!("selectedOptions" in merchandise)) {
    return undefined;
  }

  const { selectedOptions } = merchandise;
  if (!Array.isArray(selectedOptions)) {
    return undefined;
  }

  const isSelectedOptionArray = selectedOptions.every(
    (option) =>
      option != null &&
      typeof option === "object" &&
      "name" in option &&
      typeof option.name === "string" &&
      "value" in option &&
      typeof option.value === "string"
  );

  return isSelectedOptionArray ? selectedOptions : undefined;
};

export const hasCartLineAttribute = (
  cartLine:
    | CartLineAttributesRead
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined,
  key: STOREFRONT_CART_ATTRIBUTES,
  valueFn?: (value: Maybe<string> | undefined) => boolean
) =>
  !!cartLine?.attributes?.some((attr) => {
    return attr?.key === key && (valueFn ? valueFn(attr?.value) : true);
  });

export const getCartLineAttributeValue = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined,
  key: STOREFRONT_CART_ATTRIBUTES
) =>
  cartLine?.attributes?.find((attr) => {
    return attr?.key === key;
  })?.value;

export const findRelatedCartSpendingGiftWithPurchaseLine = (
  lines: Array<CartLineAttributesRead | null | undefined> | null | undefined,
  spendingThreshold?: string
) => {
  return lines?.find(
    (line) =>
      line &&
      hasCartLineAttribute(
        line,
        STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE,
        (value) => {
          if (value) {
            try {
              const parsedValue = JSON.parse(value);
              return spendingThreshold
                ? parsedValue?.cartSpendingGiftThreshold === spendingThreshold
                : parsedValue?.cartSpendingGiftThreshold;
            } catch {
              return false;
            }
          }
          return false;
        }
      )
  );
};

export const getGiftWithPurchaseAttributeValue = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined,
  key?: keyof CartGiftWithPurchaseAttributeValue
) => {
  const gwpAttribute = cartLine?.attributes?.find(
    (attribute) =>
      attribute?.key === STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
  );
  try {
    const parsedValue = gwpAttribute?.value
      ? JSON.parse(gwpAttribute.value)
      : undefined;
    return key && parsedValue ? parsedValue[key] : parsedValue;
  } catch {
    return undefined;
  }
};

type CartLineAttributes = PartialDeep<
  (CartLine | ComponentizableCartLine)["attributes"],
  { recurseIntoArrays: true }
>;

type CartLineDisplayMerchandise = {
  product?: { title?: string | null } | null;
  title?: string | null;
};

/**
 * Builds the human-readable name for a cart line, used wherever an item is
 * named for assistive technology (live-region announcements, control labels).
 *
 * The variant title is appended only when it adds information: Shopify names
 * the sole variant of a single-variant product "Default Title", which must
 * never be read out, and some variants repeat the product title verbatim.
 *
 * It never returns a fragment like " - Blue": without a product title the
 * variant title stands alone. When nothing usable is available it returns
 * `fallbackName` — accessibility call sites pass the localized generic item
 * name so a nameless line never produces a bare "Remove " label.
 */
export const getCartLineDisplayName = (
  merchandise: CartLineDisplayMerchandise | undefined | null,
  fallbackName = ""
): string => {
  const productTitle = merchandise?.product?.title ?? "";
  const variantTitle = merchandise?.title;

  const variantAddsInformation =
    !!variantTitle &&
    variantTitle !== "Default Title" &&
    variantTitle !== productTitle;

  if (!variantAddsInformation) return productTitle || fallbackName;
  if (!productTitle) return variantTitle;

  return `${productTitle} - ${variantTitle}`;
};

/**
 * Derives gift badge text and GWP-specific removability from cart line attributes.
 */
export const deriveGiftBadgeState = (
  attributes: CartLineAttributes | undefined,
  defaultGiftBadgeText: string
): { giftBadgeText: string; isRemovable: boolean } => {
  const line = { attributes };

  const badgeFromAttribute = getGiftWithPurchaseAttributeValue(
    line,
    CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.GIFT_BADGE
  );

  const giftBadgeText: string = badgeFromAttribute
    ? String(badgeFromAttribute)
    : hasCartLineAttribute(line, STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE)
    ? defaultGiftBadgeText
    : "";

  const isRemovable =
    giftBadgeText === "" ||
    getGiftWithPurchaseAttributeValue(
      line,
      CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.IS_REMOVABLE
    ) === true;

  return { giftBadgeText, isRemovable };
};

/**
 * Derives the option label and color swatch handle for a gift item.
 * Used by CartGiftWithPurchaseItem to display variant details.
 */
export const deriveGiftItemOptionDisplay = (
  selectedOptions: SelectedOption[],
  isColorOption: (o: SelectedOption) => boolean,
  isDefaultOption: (o: SelectedOption) => boolean,
  handleize: (s: string) => string
): { colorSwatchHandle: string | undefined; optionLabel: string } => {
  const colorOption = selectedOptions.find(isColorOption);
  const colorSwatchHandle = colorOption
    ? handleize(colorOption.value)
    : undefined;

  const optionLabel = selectedOptions
    .filter((o) => !isDefaultOption(o))
    .slice()
    .sort((a, b) => {
      const aIsColor = isColorOption(a);
      const bIsColor = isColorOption(b);
      if (aIsColor === bIsColor) return 0;
      return aIsColor ? -1 : 1;
    })
    .map((o) => o.value)
    .join(" / ");

  return { colorSwatchHandle, optionLabel };
};

/**
 * Builds cart line inputs for one GWP tier. Used by emit and by batched multi-tier emit.
 */
export const buildGiftWithPurchaseLineInputs = (
  gwpAttributeValue: CartGiftWithPurchaseAttributeValue,
  variants: Array<Partial<CartProductVariant> & { id: string }>,
  quantity = 1
) =>
  variants.map((variant) => ({
    attributes: [
      {
        key: STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE,
        value: JSON.stringify(gwpAttributeValue),
      },
      {
        key: STOREFRONT_CART_ATTRIBUTES.MSRP,
        value: getMsrpForVariant(variant),
      },
    ],
    id: parseGid(variant.id).id,
    quantity,
  }));

/**
 * Builds cart line inputs for GWP gift variant(s) from a single eligible tier and emits ADD_TO_CART.
 */
export const emitSingleTierGwpLinesToCart = (
  gwpAttributeValue: CartGiftWithPurchaseAttributeValue,
  variants: Array<Partial<CartProductVariant> & { id: string }>,
  quantity = 1
): void => {
  const lines = buildGiftWithPurchaseLineInputs(
    gwpAttributeValue,
    variants,
    quantity
  );
  TablescapeContext.emit(TablescapeEvent.ADD_TO_CART, {
    cart: {
      lines,
      metadata: {
        ...EMPTY_ADD_TO_CART_METADATA,
        location: CartLocation.GWP_AUTO_ADD,
      },
    },
  });
};

/**
 * Build cart line inputs for multiple GWP tiers and emits ADD_TO_CART. Used when one item satisfies multiple tiers (e.g. high-price item). One cart update adds all.
 */
export const emitMultiTierGwpLinesToCart = (
  tiers: Array<{
    gwpAttributeValue: CartGiftWithPurchaseAttributeValue;
    quantity?: number;
    variants: Array<Partial<CartProductVariant> & { id: string }>;
  }>
): void => {
  const lines = tiers.flatMap(({ gwpAttributeValue, quantity, variants }) =>
    buildGiftWithPurchaseLineInputs(gwpAttributeValue, variants, quantity ?? 1)
  );
  if (lines.length === 0) return;
  TablescapeContext.emit(TablescapeEvent.ADD_TO_CART, {
    cart: {
      lines,
      metadata: {
        ...EMPTY_ADD_TO_CART_METADATA,
        location: CartLocation.GWP_AUTO_ADD,
      },
    },
  });
};

export const addIntelligemsLineItemAttribute = (
  variantId: string,
  attributes: Maybe<AttributeInput[]>
) => {
  if (!("igData" in window) || !attributes) {
    return attributes;
  }

  const igPrice = window.igData?.price?.getPriceByVariantId(variantId);
  const currencyRate = window.Shopify?.currency?.rate;

  if (!igPrice || !currencyRate) {
    return attributes;
  }

  const intelligemsLineItemAttribute = {
    key: STOREFRONT_CART_ATTRIBUTES.INTELLIGEMS_PRICE,
    value: String(
      Math.round((parseFloat(igPrice) / parseFloat(currencyRate)) * 100)
    ),
  };

  return [...attributes, intelligemsLineItemAttribute];
};

export const hasIntelligemsLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.INTELLIGEMS_PRICE);

export const hasBundlesLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) => hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.BUNDLES);

export const hasRelatedItemsLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) => hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.RELATED_ITEMS);

/**
 * True while the cart snapshot is in the transient `replace_line` bundle-transform gap:
 * the source line id from cart metadata is no longer present, so Shopify may still be
 * applying the replacement bundle line.
 *
 * Spending-threshold GWP must not react to this snapshot — excluding the transforming
 * line can make spending subtotals (excluding GWP gift lines) dip briefly even when other
 * paid lines keep the real cart above the promo threshold.
 *
 * Pair with clearing `transformMode` / `relatedCartLineId` on the Tablescape cart once
 * the expected bundle line is observed (`TablescapeEvent.RESET_ADD_TO_CART_METADATA` in
 * `useAddToCartManager`) so stale metadata does not keep this stuck true after the
 * transform completes.
 */
export const isCartInReplaceLineBundleTransformGap = ({
  cartMetadata,
  lines,
}: {
  cartMetadata: AddToCartMetadata | null | undefined;
  lines: Array<CartLineLike | null | undefined> | null | undefined;
}): boolean => {
  if (cartMetadata?.transformMode !== "replace_line") {
    return false;
  }

  const relatedCartLineId = cartMetadata.relatedCartLineId;
  if (!relatedCartLineId) {
    return false;
  }

  const list = lines ?? [];
  if (list.some((line) => line?.id === relatedCartLineId)) {
    return false;
  }

  return true;
};

export const hasTotalWithoutBMSMLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.TOTAL_WITHOUT_BMSM);

export const getTotalWithoutBMSMValue = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  parseFloat(
    cartLine?.attributes?.find((attr) => {
      return attr?.key === STOREFRONT_CART_ATTRIBUTES.TOTAL_WITHOUT_BMSM;
    })?.value ?? "0"
  ) / 100;

export const hasGiftWithPurchaseLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE);
